import {
  getGetWorkspaceIconUrlQueryKey,
  getGetWorkspaceLogoUrlQueryKey,
  useGetWorkspaceIconUrl,
  useGetWorkspaceLogoUrl,
} from "@workspace/api-client-react";
import { useMemo } from "react";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  BRANDING_URL_STALE_TIME_MS,
  type ResolvedAccent,
  hasBrandingAsset,
  resolveAccent,
  workspaceDisplayName,
} from "@/lib/workspace-branding";

export interface WorkspaceBrandingData {
  workspaceId: string | null;
  displayName: string;
  iconUrl?: string;
  logoUrl?: string;
  accent: ResolvedAccent | null;
  isLoadingAssets: boolean;
}

/**
 * Workspace branding as PLAIN DATA. No effects, no DOM writes, safe to call
 * from any number of components.
 *
 * It used to apply the accent CSS variables itself and clear them on unmount,
 * which broke as soon as a second component called it: AppLayoutShell and the
 * app-wide controller both mounted the hook, so navigating to any page without
 * AppLayout (the workspace picker, /home, /start, /account, every admin page)
 * ran the layout's cleanup and stripped the accent app-wide, while the
 * surviving owner sat on an unchanged dependency and never re-applied. That is
 * the "branding sometimes applies, sometimes not" report.
 *
 * Writing those variables is now the sole job of
 * components/branding/workspace-accent-controller.tsx.
 */
export function useWorkspaceBranding(): WorkspaceBrandingData {
  const { activeWorkspace, activeWorkspaceId } = useWorkspace();
  const workspaceId = activeWorkspaceId ?? "";
  const branding = activeWorkspace?.branding;

  const iconQuery = useGetWorkspaceIconUrl(workspaceId, {
    query: {
      enabled: !!workspaceId && hasBrandingAsset(branding, "icon"),
      queryKey: getGetWorkspaceIconUrlQueryKey(workspaceId),
      staleTime: BRANDING_URL_STALE_TIME_MS,
    },
  });

  const logoQuery = useGetWorkspaceLogoUrl(workspaceId, {
    query: {
      enabled: !!workspaceId && hasBrandingAsset(branding, "logo"),
      queryKey: getGetWorkspaceLogoUrlQueryKey(workspaceId),
      staleTime: BRANDING_URL_STALE_TIME_MS,
    },
  });

  const displayName = activeWorkspace
    ? workspaceDisplayName(activeWorkspace)
    : "";

  // Memoised so the controller's effect does not re-run on every render:
  // resolveAccent returns a fresh object each call.
  const accentColor = branding?.accentColor;
  const accent = useMemo(() => resolveAccent(accentColor), [accentColor]);

  return {
    workspaceId: activeWorkspaceId,
    displayName,
    iconUrl: iconQuery.data?.url,
    logoUrl: logoQuery.data?.url,
    accent,
    isLoadingAssets: iconQuery.isLoading || logoQuery.isLoading,
  };
}
